import clsx from "clsx";
import { useGlobalSettings } from "global-stores";
import React, { FC, HTMLAttributes, PropsWithChildren } from "react";
import { isObject } from "utils";

export type TypeOptions = 1 | 2 | 3 /*| 4*/;

export const Typography: FC<
  PropsWithChildren<
    {
      fontType: TypeOptions;
      className?: string;
      heading?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";
    } & HTMLAttributes<HTMLElement>
  >
> = ({ fontType, children, className, heading, ...props }) => {
  const settings = useGlobalSettings();

  const headingClass = `_${heading ?? "p"}`;
  const headingTag = settings[`typography_${fontType}_${heading}_tag`] ?? "";

  const processedChildren = React.Children.toArray(children).map((child, index) => {
    if (typeof child === "string" || typeof child === "number") {
      switch (headingTag) {
        case "p":
          return <p className={headingClass}>{child}</p>;
        case "h1":
          return <h1 className={headingClass}>{child}</h1>;
        case "h2":
          return <h2 className={headingClass}>{child}</h2>;
        case "h3":
          return <h3 className={headingClass}>{child}</h3>;
        case "h4":
          return <h4 className={headingClass}>{child}</h4>;
        case "h5":
          return <h5 className={headingClass}>{child}</h5>;
        case "h6":
          return <h6 className={headingClass}>{child}</h6>;
        case "blockquote":
          return <blockquote className={headingClass}>{child}</blockquote>;
        case "div":
          return <div className={headingClass}>{child}</div>;
      }

      return `${child}`
        .split("</")
        .map((item, index, arr) => {
          if (index !== 0) {
            item = `</${item}`;
          }
          return item
            .replace(/<(h1|h2|h3|h4|h5|h6|p)>/gi, (match, group, ...rest) => {
              return `<${settings[`typography_${fontType}_${group}_tag`]} class="_${group}">`;
            })
            .replace(/<\/(h1|h2|h3|h4|h5|h6|p)>/gi, (match, group, ...rest) => {
              return `</${settings[`typography_${fontType}_${group}_tag`]}>`;
            });
        })
        .join("");
    }

    // @ts-ignore
    if (isObject(child) && "type" in child && typeof child.type === "string") {
      if (["p", "h1", "h2", "h3", "h4", "h5", "h6"].includes(child.type)) {
        const headingClass = `_${heading ?? child.type}`;
        const headingTag = settings[`typography_${fontType}_${child.type}_tag`];

        switch (headingTag) {
          case "p":
            return <p className={headingClass}>{child.props.children}</p>;
          case "h1":
            return <h1 className={headingClass}>{child.props.children}</h1>;
          case "h2":
            return <h2 className={headingClass}>{child.props.children}</h2>;
          case "h3":
            return <h3 className={headingClass}>{child.props.children}</h3>;
          case "h4":
            return <h4 className={headingClass}>{child.props.children}</h4>;
          case "h5":
            return <h5 className={headingClass}>{child.props.children}</h5>;
          case "h6":
            return <h6 className={headingClass}>{child.props.children}</h6>;
          case "blockquote":
            return <blockquote className={headingClass}>{child.props.children}</blockquote>;
          case "div":
            return <div className={headingClass}>{child.props.children}</div>;
        }
      }
    }

    return null;
  });

  return processedChildren?.some((child) => typeof child === "string")
    ? <div
        className={clsx(`font-group-${fontType}`, className)}
        dangerouslySetInnerHTML={{ __html: processedChildren.join("") }}
      >
        {processedChildren}
      </div>
    : <div className={clsx(`font-group-${fontType}`, className)}>{processedChildren}</div>;
};
